/**
 * Central registry of every `tree-hole.*` localStorage key.
 *
 * Hard rule (Skill §3): every persistence key MUST be added here so the
 * `verify_storage_keys` MCP check and any future audit can find them.
 * Keep names byte-for-byte compatible with legacy main.js so a user who
 * upgrades from the H5 site to the React app does NOT have to log in again.
 */

export const STORAGE_KEYS = {
  /** Auth bearer token. Same key as legacy main.js. */
  apiToken: "tree-hole.api-token.v1",

  /** Suppress dev auto-login on the current device. Same key as legacy. */
  devLogout: "tree-hole.dev-logout.v1",

  /** Active theme id ("default" | "warm" | "spring" | "night"). Same key as legacy. */
  theme: "tree-hole.theme.v1",

  /**
   * SettingsPage local mock state — H5 source (main.js:5664-5683, 5916-5932)
   * keeps these in-memory only; the React app persists them so a returning
   * device keeps the same identity / motion / density / privacy preference.
   * Shape: { defaultIdentity, reduceMotion, density, profilePrivate }.
   */
  settings: "tree-hole.settings.v1",

  /**
   * Per-user followed-board cache (Set<boardId>).
   * Legacy pattern: `tree-hole.followed-boards.v1:${userId}`.
   */
  followedBoards: (userId: string) => `tree-hole.followed-boards.v1:${userId}`,

  /**
   * Per-user followed-users cache (Set<userId>).
   * Legacy pattern: `tree-hole.followed-users.v1:${userId}`.
   */
  followedUsers: (userId: string) => `tree-hole.followed-users.v1:${userId}`,

  /**
   * Compose sheet draft auto-save (P0-3, audit §L3 / RULE 17 AP5).
   * Replaces H5 main.js:6560 `scheduleAutoSaveDraft` + `setTimeout` +
   * `apiFetch("/api/drafts", POST)` with Zustand `persist` middleware
   * writing to localStorage. Only the resumable subset of `draft` is
   * persisted (text / topic / boardIds / level / identityMode / font /
   * emotion); images, editing-mode flags, and ephemeral UI state are
   * dropped via the store's `partialize` config.
   */
  composeDraft: "tree-hole.compose-draft.v1",
} as const;

export type StaticStorageKey =
  | "apiToken"
  | "devLogout"
  | "theme"
  | "settings"
  | "composeDraft";
